/**
 * Role Hierarchy Configuration (Frontend)
 * 
 * This module defines the MANAGER/EXECUTIVE role hierarchy as per SCOPE-Role-Structure.md.
 * It provides helper functions to determine approval authority and role relationships.
 * 
 * Key Rule: Only MANAGER roles, ACCOUNTABLE_MANAGER, and SUPER_ADMIN can ACCEPT/REJECT
 * in Approval workflows. EXECUTIVE roles can review and add remarks but cannot approve.
 */

import { ROLES } from '../constants/roles';

/**
 * MANAGER roles - These roles can perform ACCEPT/REJECT actions in Approval workflows
 * (in addition to ACCOUNTABLE_MANAGER and SUPER_ADMIN which have override authority)
 */
const MANAGER_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ACCOUNTABLE_MANAGER,
  ROLES.AM_MRO,                       // AM MRO — AM-level in MRO / Planning / Store / Procurement
  ROLES.OPERATIONS_MANAGER,           // Operations Manager
  ROLES.PLANNING_MANAGER,             // CAMO Planning Manager
  ROLES.CA_MANAGER,           // CA Manager
  ROLES.CAMO_QM_MANAGER,              // CAMO QM Manager
  ROLES.MRO_MANAGER,                  // MRO Manager
  ROLES.MRO_QM_MANAGER,               // MRO QM Manager
  ROLES.STORE_INSPECTOR,                // Store Inspector (MANAGER)
  ROLES.SAFETY_COFS,          // Chief of Flight Safety
  ROLES.SAFETY_MANAGER        // Safety Manager (SMS)
];

/**
 * EXECUTIVE roles - These roles can perform Task workflows and add remarks,
 * but CANNOT perform ACCEPT/REJECT actions in Approval workflows
 */
const EXECUTIVE_ROLES = [
  ROLES.OPERATIONS_EXECUTIVE,
  ROLES.PLANNING_OFFICER,
  ROLES.CA_TECHNICAL_OFFICER, // CA Technical Officer
  ROLES.CAMO_QM_OFFICER,    // CAMO QM Officer
  ROLES.MRO_ENGINEER,         // MRO Engineer (EXECUTIVE)
  ROLES.MRO_QM_OFFICER,     // MRO QM Officer
  ROLES.STORE_EXECUTIVE,
  ROLES.SAFETY_OFFICER
];

/**
 * Check if a role has approval authority (can ACCEPT/REJECT in Approval workflows)
 * 
 * @param {string} role - The role to check
 * @returns {boolean} - True if the role can approve, false otherwise
 */
export const canApprove = (role) => {
  if (!role) return false;
  return MANAGER_ROLES.includes(role) || 
         role === ROLES.ACCOUNTABLE_MANAGER ||
         role === ROLES.AM_MRO ||
         role === ROLES.SUPER_ADMIN;
};

/**
 * Check if a role is a MANAGER role (excluding ACCOUNTABLE_MANAGER, AM_MRO, and SUPER_ADMIN)
 * 
 * @param {string} role - The role to check
 * @returns {boolean} - True if the role is a department MANAGER
 */
export const isManagerRole = (role) => {
  if (!role) return false;
  return MANAGER_ROLES.includes(role) && 
         role !== ROLES.ACCOUNTABLE_MANAGER &&
         role !== ROLES.AM_MRO &&
         role !== ROLES.SUPER_ADMIN;
};

/**
 * Check if a role is an EXECUTIVE role
 * 
 * @param {string} role - The role to check
 * @returns {boolean} - True if the role is an EXECUTIVE
 */
export const isExecutiveRole = (role) => {
  if (!role) return false;
  return EXECUTIVE_ROLES.includes(role);
};

/**
 * Get the MANAGER role for a given department role
 * Maps both MANAGER and EXECUTIVE roles to their department's MANAGER role
 * 
 * @param {string} departmentRole - The role (MANAGER or EXECUTIVE) from a department
 * @returns {string|null} - The corresponding MANAGER role, or null if not found
 */
export const getManagerRoleForDepartment = (departmentRole) => {
  if (!departmentRole) return null;
  
  const mapping = {
    // Operations
    [ROLES.OPERATIONS_MANAGER]: ROLES.OPERATIONS_MANAGER,
    [ROLES.OPERATIONS_EXECUTIVE]: ROLES.OPERATIONS_MANAGER,
    
    // Planning
    [ROLES.PLANNING_MANAGER]: ROLES.PLANNING_MANAGER,
    [ROLES.PLANNING_OFFICER]: ROLES.PLANNING_MANAGER,
    
    // CAMO
    [ROLES.CA_MANAGER]: ROLES.CA_MANAGER,
    [ROLES.CA_TECHNICAL_OFFICER]: ROLES.CA_MANAGER,
    
    // CAMO QM
    [ROLES.CAMO_QM_MANAGER]: ROLES.CAMO_QM_MANAGER,
    [ROLES.CAMO_QM_OFFICER]: ROLES.CAMO_QM_MANAGER,
    
    // MRO
    [ROLES.MRO_MANAGER]: ROLES.MRO_MANAGER,
    [ROLES.MRO_ENGINEER]: ROLES.MRO_MANAGER,
    [ROLES.AM_MRO]: ROLES.MRO_MANAGER,
    
    // MRO QM
    [ROLES.MRO_QM_MANAGER]: ROLES.MRO_QM_MANAGER,
    [ROLES.MRO_QM_OFFICER]: ROLES.MRO_QM_MANAGER,
    
    // Store
    [ROLES.STORE_INSPECTOR]: ROLES.STORE_INSPECTOR,
    [ROLES.STORE_EXECUTIVE]: ROLES.STORE_INSPECTOR,
    
    // Safety
    [ROLES.SAFETY_COFS]: ROLES.SAFETY_COFS,
    [ROLES.SAFETY_MANAGER]: ROLES.SAFETY_MANAGER,
    [ROLES.SAFETY_OFFICER]: ROLES.SAFETY_MANAGER
  };
  
  return mapping[departmentRole] || null;
};

/**
 * Check if a role belongs to a specific department
 * 
 * @param {string} role - The role to check
 * @param {string} departmentManagerRole - The MANAGER role of the department (e.g., ROLES.CA_MANAGER, ROLES.STORE_INSPECTOR)
 * @returns {boolean} - True if the role belongs to that department
 */
export const belongsToDepartment = (role, departmentManagerRole) => {
  if (!role || !departmentManagerRole) return false;
  
  const departmentRoles = {
    [ROLES.OPERATIONS_MANAGER]: [ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE],
    [ROLES.PLANNING_MANAGER]: [ROLES.PLANNING_MANAGER, ROLES.PLANNING_OFFICER],
    [ROLES.CA_MANAGER]: [ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER],
    [ROLES.CAMO_QM_MANAGER]: [ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER],
    [ROLES.MRO_MANAGER]: [ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.AM_MRO],
    [ROLES.MRO_QM_MANAGER]: [ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER, ROLES.AM_MRO],
    [ROLES.STORE_INSPECTOR]: [ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE],
    [ROLES.SAFETY_COFS]: [ROLES.SAFETY_COFS, ROLES.SAFETY_MANAGER, ROLES.SAFETY_OFFICER],
    [ROLES.SAFETY_MANAGER]: [ROLES.SAFETY_COFS, ROLES.SAFETY_MANAGER, ROLES.SAFETY_OFFICER]
  };
  
  const roles = departmentRoles[departmentManagerRole] || [];
  return roles.includes(role);
};

